/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: LicensingPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Account Menu module.
----------------------------------------------------------------*/


import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Shield, Key, Server, Calendar, AlertTriangle, CheckCircle, RefreshCw, Trash2, HelpCircle } from 'lucide-react';
import { useLicenseStatus } from './hooks/useLicenseStatus';
import { activateLicense, deactivateLicense, validateLicense } from './api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { ModuleFormHeader } from '../../components/layouts';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

export function LicensingPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError, showWarning } = useNotifications();
    const { status, loading, error, refresh } = useLicenseStatus();
    const [licenseKey, setLicenseKey] = useState('');
    const [activating, setActivating] = useState(false);
    const [deactivating, setDeactivating] = useState(false);
    const [validating, setValidating] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);

    const handleActivate = async () => {
        if (!licenseKey.trim()) {
            showError(t('licensing.messages.enterLicenseKey'));
            return;
        }

        setActivating(true);
        try {
            const result = await activateLicense(licenseKey.trim());
            if (result.success) {
                showSuccess(result.message || t('licensing.messages.activateSuccess'));
                setLicenseKey('');
                refresh();
            } else {
                showError(result.error || t('licensing.messages.activateFailed'));
            }
        } catch (err) {
            showError(t('licensing.errors.activateFailed'));
        } finally {
            setActivating(false);
        }
    };

    const handleDeactivate = async () => {
        setShowDeactivateModal(false);
        setDeactivating(true);
        try {
            const result = await deactivateLicense();
            if (result.success) {
                showSuccess(result.message || t('licensing.messages.deactivateSuccess'));
                refresh();
            } else {
                showError(result.error || t('licensing.messages.deactivateFailed'));
            }
        } catch (err) {
            showError(t('licensing.errors.deactivateFailed'));
        } finally {
            setDeactivating(false);
        }
    };

    const handleValidate = async () => {
        setValidating(true);
        try {
            const result = await validateLicense();
            if (result.valid) {
                showSuccess(result.message || t('licensing.messages.validateSuccess'));
            } else {
                showWarning(result.message || t('licensing.messages.validateFailed'));
            }
            refresh();
        } catch (err) {
            showError(t('licensing.errors.validateFailed'));
        } finally {
            setValidating(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <ModuleFormHeader
                    icon={<Shield className="h-5 w-5 text-primary" />}
                    title={t('licensing.title')}
                    backButtonText={t('common.back')}
                    onBack={() => navigate(-1)}
                />
                <div className="flex items-center justify-center min-h-[400px]">
                    <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            </div>
        );
    }

    if (error || !status) {
        return (
            <div className="p-6">
                <ModuleFormHeader
                    icon={<Shield className="h-5 w-5 text-primary" />}
                    title={t('licensing.title')}
                    backButtonText={t('common.back')}
                    onBack={() => navigate(-1)}
                />
                <Alert variant="destructive" className="mt-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <strong>Error:</strong> {error || t('licensing.errors.loadFailed')}
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    const isCommunity = status.mode === 'community';
    const isExpiring = status.days_remaining !== null && status.days_remaining <= 7;
    const extensionUsagePercent = (status.extensions_used / status.max_extensions) * 100;

    return (
        <div className="p-6 pb-20">
            {/* Standard Module Header */}
            <ModuleFormHeader
                icon={<Shield className="h-5 w-5 text-primary" />}
                title={t('licensing.title')}
                backButtonText={t('common.back')}
                onBack={() => navigate(-1)}
            />

            <div className="mt-8 space-y-6">
                {/* License Status Card */}
                <Card className="border border-border">
                    <CardHeader className="border-b border-border bg-muted/30">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                <Shield className="h-5 w-5 text-primary" />
                                {t('licensing.status.title')}
                            </CardTitle>
                            <Badge variant={isCommunity ? 'secondary' : status.status === 'active' ? 'default' : 'destructive'}>
                                {isCommunity ? t('licensing.status.community') : status.status.toUpperCase()}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Extensions */}
                            <div className="p-4 rounded-lg border border-border bg-card">
                                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                                    <Server className="h-4 w-4" />
                                    <span className="text-sm font-medium">{t('licensing.status.extensions')}</span>
                                    <SimpleTooltip content={t('licensing.tooltips.extensions')} position="top">
                                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                                    </SimpleTooltip>
                                </div>
                                <div className="text-2xl font-bold text-foreground">
                                    {status.extensions_used} / {status.max_extensions}
                                </div>
                                <div className="w-full h-2 bg-muted rounded-full mt-3">
                                    <div
                                        className={`h-full rounded-full transition-all ${extensionUsagePercent > 90 ? 'bg-destructive' :
                                            extensionUsagePercent > 70 ? 'bg-warning' : 'bg-primary'
                                            }`}
                                        style={{ width: `${Math.min(100, extensionUsagePercent)}%` }}
                                    />
                                </div>
                            </div>

                            {/* License Key */}
                            <div className="p-4 rounded-lg border border-border bg-card">
                                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                                    <Key className="h-4 w-4" />
                                    <span className="text-sm font-medium">{t('licensing.status.licenseKey')}</span>
                                    <SimpleTooltip content={t('licensing.tooltips.licenseKey')} position="top">
                                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                                    </SimpleTooltip>
                                </div>
                                <div className="text-lg font-mono text-foreground">
                                    {status.license_key || t('licensing.status.notLicensed')}
                                </div>
                            </div>

                            {/* Expiration */}
                            <div className="p-4 rounded-lg border border-border bg-card">
                                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                                    <Calendar className="h-4 w-4" />
                                    <span className="text-sm font-medium">{t('licensing.status.expires')}</span>
                                    <SimpleTooltip content={t('licensing.tooltips.expires')} position="top">
                                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                                    </SimpleTooltip>
                                </div>
                                <div className={`text-lg font-bold ${isExpiring ? 'text-warning' : 'text-foreground'}`}>
                                    {status.expires_at
                                        ? new Date(status.expires_at).toLocaleDateString()
                                        : status.mode === 'licensed' ? t('licensing.status.perpetual') : t('licensing.status.notApplicable')}
                                </div>
                                {status.days_remaining !== null && (
                                    <div className="text-sm text-muted-foreground mt-1">
                                        {status.days_remaining} {t('licensing.status.daysRemaining')}
                                    </div>
                                )}
                            </div>

                            {/* Hardware ID */}
                            <div className="p-4 rounded-lg border border-border bg-card">
                                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                                    <Server className="h-4 w-4" />
                                    <span className="text-sm font-medium">{t('licensing.status.hardwareId')}</span>
                                    <SimpleTooltip content={t('licensing.tooltips.hardwareId')} position="top">
                                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                                    </SimpleTooltip>
                                </div>
                                <div className="text-sm font-mono text-foreground truncate" title={status.hardware_id}>
                                    {status.hardware_id.substring(0, 16)}...
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 mt-6 pt-4 border-t border-border">
                            <SimpleTooltip content={t('licensing.tooltips.validate')} position="top">
                                <Button variant="outline" size="sm" onClick={handleValidate} disabled={validating}>
                                    <RefreshCw className={`h-4 w-4 mr-2 ${validating ? 'animate-spin' : ''}`} />
                                    {t('licensing.actions.validate')}
                                </Button>
                            </SimpleTooltip>
                            {!isCommunity && (
                                <SimpleTooltip content={t('licensing.tooltips.deactivate')} position="top">
                                    <Button variant="destructive" size="sm" onClick={() => setShowDeactivateModal(true)} disabled={deactivating}>
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        {t('licensing.actions.deactivate')}
                                    </Button>
                                </SimpleTooltip>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Activation Card - Only show for Community Edition */}
                {isCommunity && (
                    <Card className="border border-border">
                        <CardHeader className="border-b border-border bg-muted/30">
                            <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                <Key className="h-5 w-5 text-primary" />
                                {t('licensing.activate.title')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                            <p className="text-sm text-muted-foreground mb-4">
                                {t('licensing.activate.description')}
                            </p>
                            <div className="flex gap-4 items-end">
                                <div className="flex-1">
                                    <Label htmlFor="license-key">{t('licensing.activate.keyLabel')}</Label>
                                    <Input
                                        id="license-key"
                                        placeholder={t('licensing.activate.keyPlaceholder')}
                                        value={licenseKey}
                                        onChange={(e) => setLicenseKey(e.target.value)}
                                        className="font-mono mt-1"
                                    />
                                </div>
                                <SimpleTooltip content={t('licensing.tooltips.activate')} position="top">
                                    <Button onClick={handleActivate} disabled={activating || !licenseKey.trim()}>
                                        {activating ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                        )}
                                        {t('licensing.actions.activate')}
                                    </Button>
                                </SimpleTooltip>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Deactivate Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeactivateModal}
                onClose={() => setShowDeactivateModal(false)}
                onConfirm={handleDeactivate}
                title={t('licensing.modal.deactivateTitle')}
                message={t('licensing.modal.deactivateMessage')}
                confirmText={t('licensing.actions.deactivate')}
                cancelText={t('common.cancel')}
                isLoading={deactivating}
                type="danger"
            />
        </div>
    );
}

export default LicensingPage;
